import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import DemoModeBadge from './DemoModeBadge';

const Header = () => {
  const { user, logout } = useAuth();
  const { getItemCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/20 shadow-sm" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <h1 className="text-2xl font-bold" style={{ color: '#1F3D2B', fontFamily: 'Playfair Display, serif' }}>
              VinePass
            </h1>
            <DemoModeBadge className="hidden sm:inline-flex" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/events" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="events-nav-link">
              Events
            </Link>
            <Link to="/faq" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="faq-nav-link">
              FAQ
            </Link>
            {user && user.role === 'customer' && (
              <Link to="/my-tickets" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="my-tickets-nav-link">
                My Tickets
              </Link>
            )}
            {user && user.role === 'organiser' && (
              <Link to="/dashboard" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="dashboard-nav-link">
                Dashboard
              </Link>
            )}
            {user && user.role === 'associate' && (
              <Link to="/associate/events" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="associate-events-nav-link">
                My events
              </Link>
            )}
            {user && user.role === 'admin' && (
              <>
                <Link to="/dashboard" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="admin-dashboard-link">
                  Dashboard
                </Link>
                <Link to="/admin/payouts" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="admin-payouts-link">
                  Payouts
                </Link>
                <Link to="/admin/events" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="admin-events-link">
                  Moderation
                </Link>
                <Link to="/admin/refunds" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="admin-refunds-link">
                  Refunds
                </Link>
                <Link to="/admin/audit-log" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="admin-audit-log-link">
                  Audit Log
                </Link>
                <Link to="/admin/diagnostics" className="text-[#4A5D50] hover:text-[#1F3D2B] font-medium transition-colors" data-testid="admin-diagnostics-link">
                  Diagnostics
                </Link>
              </>
            )}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/cart" className="relative" data-testid="cart-button">
              <ShoppingCart className="w-6 h-6 text-[#1F3D2B]" />
              {getItemCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#C9A27A] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {getItemCount()}
                </span>
              )}
            </Link>

            {user ? (
              <div className="flex items-center space-x-3">
                <span className="text-[#4A5D50] text-sm" data-testid="user-name">Hello, {user.name}</span>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  size="sm"
                  className="text-[#4A5D50] hover:text-[#1F3D2B]"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  className="text-[#1F3D2B]"
                  data-testid="login-button"
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-8 py-3 font-medium transition-all shadow-md"
                  data-testid="register-button"
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button — shows a small badge with the cart count
              so guests always have a glanceable indicator on small screens. */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-[#1F3D2B] relative"
            data-testid="mobile-menu-button"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            {!mobileMenuOpen && getItemCount() > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#1F3D2B] text-white text-[10px] font-semibold leading-[18px] text-center ring-2 ring-white shadow-sm"
                data-testid="mobile-menu-cart-badge"
                aria-label={`${getItemCount()} ticket${getItemCount() === 1 ? '' : 's'} in cart`}
              >
                {getItemCount() > 99 ? '99+' : getItemCount()}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-[#E5E9E6]" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            <Link
              to="/events"
              className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Events
            </Link>
            <Link
              to="/faq"
              className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="faq-mobile-nav-link"
            >
              FAQ
            </Link>
            {user && (user.role === 'organiser' || user.role === 'admin') && (
              <Link
                to="/dashboard"
                className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Dashboard
              </Link>
            )}
            {user && user.role === 'admin' && (
              <>
                <Link
                  to="/admin/payouts"
                  className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Payouts
                </Link>
                <Link
                  to="/admin/events"
                  className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Moderation
                </Link>
                <Link
                  to="/admin/refunds"
                  className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Refunds
                </Link>
                <Link
                  to="/admin/audit-log"
                  className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Audit Log
                </Link>
                <Link
                  to="/admin/diagnostics"
                  className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Diagnostics
                </Link>
              </>
            )}
            {user && user.role === 'associate' && (
              <Link
                to="/associate/events"
                className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                My events
              </Link>
            )}
            {user && user.role === 'customer' && (
              <Link
                to="/my-tickets"
                className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                My Tickets
              </Link>
            )}
            <Link
              to="/cart"
              className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Cart ({getItemCount()})
            </Link>
            {user ? (
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left text-[#4A5D50] hover:text-[#1F3D2B] font-medium pt-3 border-t border-[#E5E9E6]"
                data-testid="mobile-logout-button"
              >
                Logout ({user.name})
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="block text-[#4A5D50] hover:text-[#1F3D2B] font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;